// AdminAuth.jsx — guards the admin panel; renders children only for allowlisted emails.
import React, { useContext } from 'react';
import { AuthContext } from '../auth/AuthProvider.jsx';

const ADMIN_EMAIL_ALLOWLIST = ['markytowers@gmail.com', 'marky_towers@yahoo.co.uk'];

export function isAdminEmail(email) {
  return ADMIN_EMAIL_ALLOWLIST.includes((email || '').toLowerCase());
}

export default function AdminAuth({ children }) {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--c-text-3)', fontFamily: 'var(--font-body)' }}>
        Loading…
      </div>
    );
  }

  // DEV-only screenshot bypass — Vite tree-shakes this entire branch out of
  // production bundles (import.meta.env.DEV is statically false after build).
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('admin_screenshot_bypass')) {
    return children;
  }

  // Developer-mode bypass — works in production builds on the user's own device.
  // Requires BOTH tend:devmode and tend:adminDevBypass to be set so an
  // accidental localStorage key never silently grants access.
  const devmode   = (typeof localStorage !== 'undefined') && localStorage.getItem('tend:devmode')      === 'true';
  const adminBypass = (typeof localStorage !== 'undefined') && localStorage.getItem('tend:adminDevBypass') === 'true';
  if (devmode && adminBypass) {
    return (
      <>
        <div style={{
          background: 'var(--c-warn-bg, #f0e8d0)',
          color: 'var(--c-warn-text, #6b4e18)',
          padding: '6px 12px',
          fontSize: '12px',
          textAlign: 'center',
          borderBottom: '1px solid var(--c-border)',
        }}>
          Developer-mode access — not authenticated. Some live data may be unavailable.
        </div>
        {children}
      </>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'var(--font-body)', color: 'var(--c-text-2)' }}>
        <p style={{ marginBottom: 12, color: 'var(--c-text)' }}>Sign in to access the admin panel.</p>
      </div>
    );
  }

  if (!isAdminEmail(user.email)) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'var(--font-body)', color: 'var(--c-text-2)' }}>
        <p style={{ color: 'var(--c-danger)', marginBottom: 8 }}>Not authorised</p>
        <p style={{ fontSize: 'var(--font-small)' }}>Admin access is restricted. Signed in as <strong>{user.email}</strong>.</p>
      </div>
    );
  }

  return children;
}
